import { Suspense } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { AuthGuard } from "@/components/AuthGuard";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { adminManifest, buildElement } from "@/components/layout/adminManifest";

/**
 * Root component — admin SPA routing.
 *
 * The route table is generated from `adminManifest` in one place so the
 * sidebar + routes stay in sync: every admin URL listed in the manifest
 * gets a Route entry here, either to the real ported page or to
 * `<ModuleStub/>` for modules that haven't been ported yet.
 *
 * Nesting: all routes are children of `<AdminLayout/>`, which renders
 * the sidebar + top bar + `<Outlet/>`. `<AuthGuard/>` wraps the whole
 * tree so unauthenticated visitors redirect before any chrome renders.
 */
export function App() {
  return (
    <AuthGuard>
      <Suspense fallback={<RouteSpinner />}>
        <Routes>
          <Route element={<AdminLayout />}>
            {adminManifest.map((m) => {
              const element =
                m.path === "/" ? <Dashboard /> : buildElement(m);
              return (
                <Route key={m.path} path={m.path} element={element} />
              );
            })}
            {adminManifest.flatMap((m) =>
              (m.extraRoutes ?? []).map((r) => (
                <Route key={r.path} path={r.path} element={r.element} />
              )),
            )}
            <Route path="/admin/*" element={<NotFoundInLayout />} />
            <Route path="*" element={<NotFoundInLayout />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthGuard>
  );
}

function Dashboard() {
  return (
    <div className="max-w-3xl space-y-4">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        KnowledgeGate Admin Portal
      </h1>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Pick a module from the sidebar. Admin modules are being ported
        in from KGAI per the extraction plan (see{" "}
        <code>docs/admin-extraction-plan.md</code>) — more will appear
        here as they land.
      </p>
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-xs text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-500">
        During the copy-first migration, the live admin continues at{" "}
        <code>admin.knowledgegate.ai</code> served from KGAI&apos;s Next.js
        bundle. This SPA takes over only after the Checkpoint E cutover.
      </div>
    </div>
  );
}

function NotFoundInLayout() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center space-y-2">
        <p className="text-4xl font-bold text-gray-400 dark:text-gray-600">
          404
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          This route doesn&apos;t exist in the admin SPA.{" "}
          <Link to="/" className="text-purple-600 underline hover:text-purple-700">
            Go to dashboard
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function RouteSpinner() {
  return (
    <div className="flex min-h-full items-center justify-center p-8">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500" />
    </div>
  );
}
